<template>
  <div class="credit-dashboard">
    <!-- Header -->
    <div class="dashboard-header">
      <h1>Procurement Credit Dashboard</h1>
      <p class="subtitle">Monitor and manage payment credit periods</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="spinner"></div>
      <p>Loading credit metrics...</p>
    </div>

    <!-- Main Content -->
    <template v-else>
      <!-- Metric Cards -->
      <div class="metrics-grid">
        <CreditMetricCard
          v-for="(metric, key) in metrics"
          :key="key"
          :metric="metric"
          :label="metricLabels[key]"
          @view-details="selectedMetricKey = key"
        />
      </div>

      <!-- Charts Row -->
      <div class="charts-row">
        <div class="chart-container">
          <CreditTimelineChart :timeline-data="timelineData" />
        </div>

        <div class="chart-container">
          <CreditMetricsChart :metrics="metrics" />
        </div>
      </div>

      <!-- Details Section -->
      <div v-if="selectedMetricKey" class="details-section">
        <div class="details-header">
          <h2>{{ metricLabels[selectedMetricKey] }}</h2>
          <button class="close-btn" @click="selectedMetricKey = null">✕</button>
        </div>

        <div v-if="metrics[selectedMetricKey].purchases.length > 0" class="purchases-list">
          <table>
            <thead>
              <tr>
                <th>Purchase No</th>
                <th>Supplier</th>
                <th>Amount</th>
                <th>Due Date</th>
                <th>Days Left</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="purchase in metrics[selectedMetricKey].purchases" :key="purchase.id">
                <td class="purchase-no">{{ purchase.purchaseNo }}</td>
                <td class="supplier">{{ purchase.supplier }}</td>
                <td class="amount">{{ formatCurrency(purchase.amount) }}</td>
                <td class="due-date">{{ formatDate(purchase.expiryDate) }}</td>
                <td class="days-left">{{ purchase.daysLeft }}</td>
                <td class="status">
                  <span :class="getStatusClass(purchase.daysLeft)">
                    {{ getStatusText(purchase.daysLeft) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="no-data">
          <p>No purchases in this category</p>
        </div>
      </div>

      <!-- Summary Stats -->
      <div class="summary-stats">
        <div class="stat-card">
          <h3>Total Credits</h3>
          <p class="stat-value">{{ metrics.summary?.totalCredits || 0 }}</p>
        </div>
        <div class="stat-card highlighted">
          <h3>Total Amount</h3>
          <p class="stat-value">{{ formatCurrency(metrics.summary?.totalAmount || 0) }}</p>
        </div>
        <div class="stat-card critical">
          <h3>Critical</h3>
          <p class="stat-value">{{ metrics.summary?.criticalCount || 0 }}</p>
          <p class="stat-label">(Due in 1 day or overdue)</p>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import CreditMetricCard from './components/CreditMetricCard.vue';
import CreditTimelineChart from './components/CreditTimelineChart.vue';
import CreditMetricsChart from './components/CreditMetricsChart.vue';

const loading = ref(false);
const metrics = ref({});
const timelineData = ref({});
const selectedMetricKey = ref(null);

const metricLabels = {
  dueIn30Days: 'Due in 1 Month',
  dueIn7Days: 'Due in 1 Week',
  dueIn1Day: 'Due in 1 Day',
  overdue: 'Overdue Payments',
};

// Format currency
const formatCurrency = (value) => {
  if (!value) return '₦0.00';
  return `₦${(value / 1000).toFixed(1)}K`;
};

// Format date
const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// Get status class
const getStatusClass = (daysLeft) => {
  if (daysLeft <= 0) return 'status-overdue';
  if (daysLeft === 1) return 'status-critical';
  if (daysLeft <= 7) return 'status-urgent';
  return 'status-warning';
};

// Get status text
const getStatusText = (daysLeft) => {
  if (daysLeft <= 0) return 'Overdue';
  if (daysLeft === 1) return 'Critical';
  if (daysLeft <= 7) return 'Urgent';
  return 'Warning';
};

// Fetch metrics
const fetchMetrics = async () => {
  loading.value = true;
  try {
    const [metricsRes, timelineRes] = await Promise.all([
      fetch('/api/purchase-credit/metrics/dashboard'),
      fetch('/api/purchase-credit/chart/timeline'),
    ]);

    const metricsData = await metricsRes.json();
    const timelineDataResponse = await timelineRes.json();

    metrics.value = metricsData.metrics || {};
    metrics.value.summary = metricsData.summary || {};
    timelineData.value = timelineDataResponse.timeline || [];
  } catch (error) {
    console.error('Error fetching metrics:', error);
  } finally {
    loading.value = false;
  }
};

// Lifecycle
onMounted(() => {
  fetchMetrics();
});
</script>

<style scoped>
.credit-dashboard {
  padding: 20px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
}

.dashboard-header {
  margin-bottom: 30px;
  text-align: center;
}

.dashboard-header h1 {
  font-size: 32px;
  color: #333;
  margin: 0 0 10px 0;
  font-weight: 700;
}

.subtitle {
  color: #666;
  font-size: 16px;
  margin: 0;
}

/* Metrics Grid */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

/* Charts Row */
.charts-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 30px;
}

.chart-container {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Details Section */
.details-section {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
}

.details-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 2px solid #eee;
  padding-bottom: 15px;
}

.details-header h2 {
  margin: 0;
  color: #333;
  font-size: 20px;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  transition: color 0.3s;
}

.close-btn:hover {
  color: #333;
}

/* Purchases Table */
.purchases-list table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.purchases-list thead {
  background: #f9f9f9;
  font-weight: 600;
  color: #333;
}

.purchases-list th,
.purchases-list td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.purchases-list tr:hover {
  background: #f5f5f5;
}

.purchase-no {
  font-weight: 600;
  color: #0066cc;
}

.supplier {
  color: #666;
}

.amount {
  font-weight: 600;
  color: #27ae60;
}

.due-date {
  color: #666;
}

.days-left {
  font-weight: 600;
}

.status {
  text-align: center;
}

.status-overdue {
  background: #fee;
  color: #c00;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.status-critical {
  background: #fdd;
  color: #d00;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.status-urgent {
  background: #ffc;
  color: #a00;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
}

.status-warning {
  background: #efe;
  color: #060;
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
}

/* No Data */
.no-data {
  text-align: center;
  padding: 40px 20px;
  color: #999;
}

/* Summary Stats */
.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.stat-card {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.stat-card h3 {
  margin: 0 0 10px 0;
  color: #666;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.stat-value {
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  color: #333;
}

.stat-label {
  margin: 5px 0 0 0;
  color: #999;
  font-size: 12px;
}

.stat-card.highlighted {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.stat-card.highlighted h3,
.stat-card.highlighted .stat-label {
  color: rgba(255, 255, 255, 0.8);
}

.stat-card.highlighted .stat-value {
  color: white;
}

.stat-card.critical {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
}

.stat-card.critical h3,
.stat-card.critical .stat-label {
  color: rgba(255, 255, 255, 0.8);
}

.stat-card.critical .stat-value {
  color: white;
}

/* Loading */
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #667eea;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 1024px) {
  .charts-row {
    grid-template-columns: 1fr;
  }

  .metrics-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
}

@media (max-width: 640px) {
  .credit-dashboard {
    padding: 10px;
  }

  .dashboard-header h1 {
    font-size: 24px;
  }

  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .purchases-list table {
    font-size: 12px;
  }

  .purchases-list th,
  .purchases-list td {
    padding: 8px;
  }
}
</style>
